/**
 * @file AuthContext.js
 * @description React Context Provider managing authentication state, tenant schema isolation,
 * user session refreshing, and role-based access flags (isAdmin, isApprover, isResponder, isCreator).
 */

import React, { createContext, useContext, useState, useEffect } from 'react';
import ApiService from '../services/api';

const AuthContext = createContext();

/**
 * Custom React hook to consume the AuthContext.
 *
 * @returns {Object} Authentication state and methods (user, hospital, login, logout, role flags).
 * @throws {Error} If called outside of an AuthProvider.
 */
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

/**
 * AuthProvider component that wraps the application and provides global auth state.
 *
 * @component
 * @param {Object} props - Component properties.
 * @param {React.ReactNode} props.children - Child components requiring auth context.
 * @returns {React.ReactElement} Provider-wrapped element.
 */
const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [hospital, setHospital] = useState(null);
  const [loading, setLoading] = useState(true);
  const [apiService] = useState(new ApiService());

  useEffect(() => {
    initializeAuth();
  }, []);

  /**
   * Restores cached session from localStorage, validates subdomain tenant match,
   * and refreshes active user/hospital records from backend.
   */
  const initializeAuth = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const storedUser = JSON.parse(localStorage.getItem('user') || 'null');
      const storedHospital = JSON.parse(localStorage.getItem('hospital') || 'null');

      if (token && storedUser && storedHospital) {
        // Guard against cross-tenant session bleed when navigating between subdomains
        const hostname = window.location.hostname;
        const parts = hostname.split('.');
        let currentSubdomain = '';
        if (parts.length >= 3) {
          const first = parts[0].toLowerCase();
          if (!['web', 'app', 'www', 'demoapp'].includes(first)) currentSubdomain = first;
        } else if (parts.length === 2 && parts[1] === 'localhost') {
          const first = parts[0].toLowerCase();
          if (first !== 'localhost') currentSubdomain = first;
        }

        if (currentSubdomain && storedHospital.schema_name && storedHospital.schema_name !== currentSubdomain) {
          console.warn(`[AuthContext] Session (${storedHospital.schema_name}) does not match URL subdomain (${currentSubdomain}). Resetting.`);
          localStorage.removeItem('authToken');
          localStorage.removeItem('user');
          localStorage.removeItem('hospital');
          localStorage.removeItem('tenant_slug');
          setUser(null);
          setHospital(null);
          setLoading(false);
          return;
        }

        setUser(storedUser);
        setHospital(storedHospital);

        // Fetch latest data from backend on page load
        try {
          const fresh = await apiService.refreshUser();
          setUser(fresh.user);
          setHospital(fresh.hospital);
          localStorage.setItem('user', JSON.stringify(fresh.user));
          localStorage.setItem('hospital', JSON.stringify(fresh.hospital));
        } catch (err) {
          // Token was revoked (another device logged in) → force logout
          console.error('Session expired:', err);
          logout();
          return;
        }
      }
    } catch (error) {
      console.error('Failed to initialize auth:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  /**
   * Log in a user via phone number, MPIN, and optional FCM device token.
   *
   * @param {string} phoneNumber - User's 10-digit phone number.
   * @param {string} mpin - User's 6-digit MPIN.
   * @param {string|null} [fcmToken=null] - Optional Firebase device notification token.
   * @returns {Promise<{success: boolean, error?: string}>} Login outcome.
   */
  const login = async (phoneNumber, mpin, fcmToken) => {
    try {
      const response = await apiService.login(phoneNumber, mpin, fcmToken);
      const { token, user: userData, hospital: hospitalData } = response;

      localStorage.setItem('authToken', token);
      localStorage.setItem('user', JSON.stringify(userData));
      localStorage.setItem('hospital', JSON.stringify(hospitalData));

      setUser(userData);
      setHospital(hospitalData);

      return { success: true };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  /**
   * Log out the current user, revoke backend auth token, and clear stored session.
   */
  const logout = async () => {
    await apiService.logout();
    localStorage.removeItem('authToken');
    localStorage.removeItem('user');
    localStorage.removeItem('hospital');
    setUser(null);
    setHospital(null);
  };

  /**
   * Update active user, hospital, and token states in React state and localStorage.
   *
   * @param {Object} newUser - Updated user profile.
   * @param {Object} newHospital - Updated hospital metadata.
   * @param {string} newToken - Fresh auth token.
   */
  const updateAuth = (newUser, newHospital, newToken) => {
    setUser(newUser);
    setHospital(newHospital);
    localStorage.setItem('user', JSON.stringify(newUser));
    localStorage.setItem('hospital', JSON.stringify(newHospital));
    localStorage.setItem('authToken', newToken);
  };

  /**
   * Send an OTP to the user's phone for setting or resetting their MPIN.
   *
   * @param {string} phoneNumber - Target phone number.
   * @param {string} newMpin - Desired new MPIN.
   * @returns {Promise<{success: boolean, data?: any, error?: string}>} Operation outcome.
   */
  const setPasswordfunc = async (phoneNumber, newMpin) => {
    try {
      const data = await apiService.send_otp(phoneNumber, newMpin);
      return { success: true, data };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  /**
   * Verify an OTP and commit the new MPIN.
   *
   * @param {string} phoneNumber - Registered phone number.
   * @param {string} otp - Received 6-digit OTP.
   * @returns {Promise<{success: boolean, data?: any, error?: string}>} Operation outcome.
   */
  const resetpasswordfunc = async (phoneNumber, otp) => {
    try {
      const data = await apiService.verifyOtp(phoneNumber, otp);
      return { success: true, data };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const value = {
    user,
    hospital,
    loading,
    login,
    logout,
    updateAuth,
    setPasswordfunc,
    resetpasswordfunc,
    apiService,
    isAuthenticated: !!user,
    isAdmin: user?.is_admin || user?.is_staff || user?.role === 'ADMIN' || false,
    isSuperuser: user?.is_superuser || user?.is_staff || (user?.is_creator && user?.is_approver && user?.is_responder) || false,
    isApprover: user?.is_approver || false,
    isResponder: user?.is_responder || false,
    isCreator: user?.is_creator || false,
    isRaiser: user?.is_raiser || false,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export { AuthContext, AuthProvider };
